import type { FieldFilter, Filter, FilterPathSegment, GlobalFieldId, GlobalPrimitiveFieldId, GlobalSingleRelationFieldId, PrimitiveFieldPath, PrimitivePathSegment, SegmentedFieldFilter, ToManyPathSegment, ToOnePathSegment } from '#layers/data-model/public';
import type {
	FilterSchemaCollection,
	FilterSchemaField,
	FilterSchemaPrimitiveField,
} from './filter-schema';
import { FilterRelationalQuantifier, isFilterOperator, isFilterRelationalQuantifier } from '#layers/data-model/public';

export function isPrimitiveSegment(s: FilterPathSegment): s is PrimitivePathSegment {
	return s.type === 'primitive';
}

export function isToOneSegment(s: FilterPathSegment): s is ToOnePathSegment {
	return s.type === 'to-one';
}

export function isToManySegment(s: FilterPathSegment): s is ToManyPathSegment {
	return s.type === 'to-many';
}

export function isRelationalSegment(s: FilterPathSegment): s is ToOnePathSegment | ToManyPathSegment {
	return isToOneSegment(s) || isToManySegment(s);
}

function isPrimitiveFilterField(field: FilterSchemaField): field is FilterSchemaPrimitiveField {
	return 'type' in field && 'dbName' in field;
}

/**
 * Whether a selected field path ends on a primitive field (vs. a relation). A relation-terminated
 * path is handled as a related-item match rather than a plain field filter, so the field-path chips
 * use this to route a `CollectionFieldPathSelect` selection to the right branch.
 */
export function isPrimitiveFieldPath(
	fieldPath: readonly GlobalFieldId[],
	schemaLookup: Pick<FilterSchemaLookup, 'findFieldById'>,
): fieldPath is PrimitiveFieldPath {
	const terminalId = fieldPath.at(-1);
	if (!terminalId) return false;

	const field = schemaLookup.findFieldById(terminalId);
	return !!field && isPrimitiveFilterField(field);
}

// Lookup interface used by filter parsing and traversal helpers.
export interface FilterSchemaLookup {
	findFieldById: (id: GlobalFieldId) => FilterSchemaField | undefined;
	findFieldByApiName: (collection: FilterSchemaCollection, apiName: string) => FilterSchemaField | undefined;
	findOppositeCollectionByRelationFieldId: (id: GlobalSingleRelationFieldId) => FilterSchemaCollection | undefined;
	/** Resolves an M2M field to the target collection the user picks from (undefined for non-M2M relations). */
	findM2MTargetCollectionByRelationFieldId?: (id: GlobalSingleRelationFieldId) => FilterSchemaCollection | undefined;
}

export function serializeSegmentedFilter(
	filter: SegmentedFieldFilter,
	schemaLookup: FilterSchemaLookup,
): FieldFilter {
	const { segments, operator, value } = filter;

	if (segments.length === 0) {
		throw new Error('SegmentedFieldFilter must have at least one segment');
	}

	const lastSegment = segments.at(-1)!;

	if (!isPrimitiveSegment(lastSegment)) {
		throw new Error('SegmentedFieldFilter must end with a primitive segment');
	}

	return segments.reduceRight((inner, segment) => {
		const field = schemaLookup.findFieldById(segment.fieldId);

		if (!field) {
			throw new Error(`Field not found: ${segment.fieldId}`);
		}

		const apiName = field.apiName;

		if (isToManySegment(segment)) {
			return { [apiName]: { [segment.quantifier]: inner } };
		}

		return { [apiName]: inner };
	}, operator ? { [operator]: value } : {} as FieldFilter);
}

export function parseSegmentedFilter(
	filter: FieldFilter,
	collection: FilterSchemaCollection,
	schemaLookup: FilterSchemaLookup,
): SegmentedFieldFilter {
	const segments: FilterPathSegment[] = [];
	let currentCollection = collection;
	let currentObj = filter;

	while (true) {
		const keys = Object.keys(currentObj);

		if (keys.length === 0) {
			// Empty object = incomplete filter (operator/value lost to JSON serialization)
			return { segments, operator: undefined, value: undefined };
		}

		const key = keys[0]!;

		if (isFilterOperator(key)) {
			return { segments, operator: key, value: currentObj[key] };
		}

		const field = schemaLookup.findFieldByApiName(currentCollection, key);

		if (!field) {
			throw new Error(`Unknown field '${key}' in collection '${currentCollection.apiName}'`);
		}

		if (isPrimitiveFilterField(field)) {
			segments.push({ type: 'primitive', fieldId: field.id as GlobalPrimitiveFieldId });
			currentObj = currentObj[key] as FieldFilter;
		}
		else if (field.isList) {
			const inner = currentObj[key] as Record<string, unknown>;
			const firstInnerKey = Object.keys(inner)[0];
			const hasExplicitQuantifier = firstInnerKey && isFilterRelationalQuantifier(firstInnerKey);
			const quantifier = hasExplicitQuantifier ? firstInnerKey : '_some';

			segments.push({ type: 'to-many', fieldId: field.id as GlobalSingleRelationFieldId, quantifier });
			currentObj = (hasExplicitQuantifier ? inner[quantifier] : inner) as FieldFilter;
		}
		else {
			segments.push({ type: 'to-one', fieldId: field.id as GlobalSingleRelationFieldId });
			currentObj = currentObj[key] as FieldFilter;
		}

		if (!isPrimitiveFilterField(field)) {
			const oppositeCollection = schemaLookup.findOppositeCollectionByRelationFieldId(field.id as GlobalSingleRelationFieldId);

			if (!oppositeCollection) {
				throw new Error(`[parseSegmentedFilter]: could not determine opposite collection of ${field.apiName}(${field.id})`);
			}

			currentCollection = oppositeCollection;
		}
	}
}

export interface ResolvedFieldPathSelection {
	/** The kept prefix (segments before `index`) plus the newly-selected path. */
	segments: FilterPathSegment[];
	/** The terminal field when the selection ends in a primitive, for seeding a default operator. */
	terminalPrimitiveField: FilterSchemaPrimitiveField | undefined;
}

/**
 * Rebuilds a field path when the selector at `index` picks a new field: keeps the segments before
 * `index`, resolves the collection to build from, and appends the new path. Returns `null` when the
 * selection yields no segments. Shared by the field-path chips in `FilterSimple` and
 * `FilterRelatedItem`.
 */
export function resolveFieldPathSegments(
	existingSegments: FilterPathSegment[],
	index: number,
	fieldPath: PrimitiveFieldPath,
	collection: FilterSchemaCollection,
	schemaLookup: FilterSchemaLookup,
): ResolvedFieldPathSelection | null {
	const keptSegments = existingSegments.slice(0, index);
	const lastKept = keptSegments.at(-1);
	const startCollection = lastKept && isRelationalSegment(lastKept)
		? schemaLookup.findOppositeCollectionByRelationFieldId(lastKept.fieldId) ?? collection
		: collection;
	const newSegments = buildSegmentsFromFieldIds(fieldPath, startCollection, schemaLookup);

	if (newSegments.length === 0) return null;

	const lastSegment = newSegments.at(-1)!;
	const terminalField = isPrimitiveSegment(lastSegment) ? schemaLookup.findFieldById(lastSegment.fieldId) : undefined;
	const terminalPrimitiveField = terminalField && isPrimitiveFilterField(terminalField) ? terminalField : undefined;

	return { segments: [...keptSegments, ...newSegments], terminalPrimitiveField };
}

/**
 * Nests a terminal filter back through a relation path, wrapping each hop as `{ [apiName]: inner }`
 * and applying a relational quantifier at to-many hops. Returns `null` when a relation cannot be
 * resolved. Shared by the create path (`useRelatedItemFilter`) and the edit path
 * (`useRelatedItemMatch`).
 *
 * By default every to-many hop uses `_some`. `options.quantifier` overrides the quantifier at the
 * deepest to-many hop (e.g. `_none` to exclude, `_every` to require all); every other to-many hop
 * stays `_some` (it only means "traverse into the list"). The deepest to-many hop is used — not
 * merely the last path hop — so an M2M path (`m2m` to-many → junction-to-target to-one) still
 * applies the quantifier at the list hop.
 */
export function nestFilterThroughRelations(
	relationFieldPath: string[],
	terminal: Filter,
	resolveRelation: (id: GlobalSingleRelationFieldId) => { apiName: string; isList: boolean } | undefined,
	options: { quantifier?: FilterRelationalQuantifier } = {},
): Filter | null {
	const { quantifier = FilterRelationalQuantifier.Some } = options;

	const relations = relationFieldPath.map((id) => resolveRelation(id as GlobalSingleRelationFieldId));
	if (relations.includes(undefined)) return null;

	let lastToManyIndex = -1;

	for (const [index, relation] of relations.entries()) {
		if (relation!.isList) lastToManyIndex = index;
	}

	let condition: Filter = terminal;

	for (let index = relationFieldPath.length - 1; index >= 0; index--) {
		const relation = relations[index]!;

		if (relation.isList) {
			const hopQuantifier = index === lastToManyIndex ? quantifier : FilterRelationalQuantifier.Some;
			condition = { [relation.apiName]: { [hopQuantifier]: condition } };
		}
		else {
			condition = { [relation.apiName]: condition };
		}
	}

	return condition;
}

export function buildSegmentsFromFieldIds(
	fieldPath: PrimitiveFieldPath,
	collection: FilterSchemaCollection,
	schemaLookup: Pick<FilterSchemaLookup, 'findOppositeCollectionByRelationFieldId'>,
): FilterPathSegment[] {
	const segments: FilterPathSegment[] = [];
	let currentCollection = collection;

	for (const fieldId of fieldPath) {
		const primitive = currentCollection.primitiveFields.find((f) => f.id === fieldId);

		if (primitive) {
			segments.push({ type: 'primitive', fieldId: fieldId as GlobalPrimitiveFieldId });
			break; // Primitive is always last
		}

		const relation = currentCollection.relationFields?.find((f) => f.id === fieldId);

		if (relation) {
			if (relation.isList) {
				segments.push({ type: 'to-many', fieldId: fieldId as GlobalSingleRelationFieldId, quantifier: '_some' });
			}
			else {
				segments.push({ type: 'to-one', fieldId: fieldId as GlobalSingleRelationFieldId });
			}

			const oppositeCollection = schemaLookup.findOppositeCollectionByRelationFieldId(fieldId as GlobalSingleRelationFieldId);

			if (!oppositeCollection) {
				throw new Error(`[buildSegmentsFromFieldIds]: Could not determine opposite collection for relation field '${relation.apiName}' (${relation.id})`);
			}

			currentCollection = oppositeCollection;
		}
		else {
			throw new Error(`[buildSegmentsFromFieldIds]: Unknown field ID '${fieldId}' in collection '${currentCollection.apiName}'`);
		}
	}

	return segments;
}

/**
 * Maps a resolved relation-hop path (as stored on a related-item clause) to renderable field-path
 * segments, preserving each hop's cardinality. A to-many hop keeps its `_some` quantifier so that
 * re-targeting the path through `serializeSegmentedFilter` doesn't drop the quantifier and emit an
 * invalid to-many filter.
 */
export function buildRelationPathSegments(
	relationFieldPath: string[],
	resolveRelation: (id: GlobalSingleRelationFieldId) => { isList: boolean } | undefined,
): FilterPathSegment[] {
	return relationFieldPath.map((fieldId): FilterPathSegment => {
		const id = fieldId as GlobalSingleRelationFieldId;

		return resolveRelation(id)?.isList
			? { type: 'to-many', fieldId: id, quantifier: FilterRelationalQuantifier.Some }
			: { type: 'to-one', fieldId: id };
	});
}

export type { FilterPathSegment, PrimitivePathSegment, SegmentedFieldFilter, ToManyPathSegment, ToOnePathSegment } from '#layers/data-model/public';
